import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"
import { round_with_spaces, format_thousands } from "../../../utils/functions"
import { Fragment } from "react"
import stats_font_sizes from "../../../configs/stats_font_sizes.json"
import theme_colors from "../../../configs/theme_colors.json"

const CustomLegendHIV = (periods) => {
  return (
    <div style={{ fontSize: 13 }}>
      {periods.periods.map((period, index) => {
        const colour = Object.values(theme_colors)[index]
        return (
          <Fragment key={period}>
            <span style={{ color: colour, paddingLeft: 10 }}>■</span> {period}
          </Fragment>
        )
      })}
    </div>
  )
}

function CustomTooltipHIV({ payload, label, active }) {
  if (active) {
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "white",
          fontSize: stats_font_sizes.tooltip_font_size,
          padding: 4,
          border: "black",
          boxShadow: "4px 6px 3px rgba(0, 0, 0, 0.2)",
        }}
      >
        <p style={{ padding: 0, margin: 0, fontWeight: "bold" }}>{label}</p>
        {payload.map(({ name, value }) => {
          return (
            <Fragment key={name}>
              <p
                style={{ padding: 0, margin: 0 }}
              >{`${name}: ${round_with_spaces(value)} people`}</p>
            </Fragment>
          )
        })}
      </div>
    )
  }

  return null
}

export default function HIVBarChart({ stats }) {
  const { hiv } = stats
  const periods = hiv.map(({ name }) => {
    return name
  })

  const data = [
    {
      name: `Total remaining on ART`,
      ...Object.fromEntries(hiv.map((item) => [item.name, item.curr])),
    },
    {
      name: "New on ART",
      ...Object.fromEntries(hiv.map((item) => [item.name, item.new])),
    },
    {
      name: "Return to treatment",
      ...Object.fromEntries(hiv.map((item) => [item.name, item.return])),
    },
    {
      name: "Transfer out",
      ...Object.fromEntries(hiv.map((item) => [item.name, item.out])),
    },
    {
      name: "Lost to follow up",
      ...Object.fromEntries(hiv.map((item) => [item.name, item.lost])),
    },
    {
      name: "Died",
      ...Object.fromEntries(hiv.map((item) => [item.name, item.died])),
    },
  ]

  return (
    <ResponsiveContainer width="100%" height="90%">
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="name"
          fontSize={stats_font_sizes.y_axis_label_font_size}
          angle={20}
          tick={({ x, y, payload }) => {
            const words = payload.value.split(" ")
            let lines = []

            if (words.length > 3) {
              // Put all but last 2 words on separate lines, then combine the last 2
              lines = [
                ...words.slice(0, words.length - 2),
                words.slice(-2).join(" "),
              ]
            } else {
              lines = words // Keep original if 3 or fewer
            }

            return (
              <g transform={`translate(${x},${y + 10})`}>
                {lines.map((word, index) => (
                  <text
                    key={index}
                    x={0}
                    y={index * 12}
                    textAnchor="middle"
                    fill="#666"
                    fontSize={12}
                  >
                    {word}
                  </text>
                ))}
              </g>
            )
          }}
        />
        <YAxis
          fontSize={stats_font_sizes.y_axis_label_font_size}
          tickFormatter={(tick) => format_thousands(tick)}
          label={{
            value: "Population",
            style: { textAnchor: "middle" },
            angle: -90,
            position: "left",
            offset: -15,
          }}
        />
        <Tooltip content={CustomTooltipHIV} />
        <Legend
          content={(props) => <CustomLegendHIV {...props} periods={periods} />}
          wrapperStyle={{
            position: "relative",
            top: -5,
          }}
        />
        {periods.map((period, index) => {
          const colour = Object.values(theme_colors)[index]
          return <Bar key={period} dataKey={period} fill={colour} />
        })}
      </BarChart>
    </ResponsiveContainer>
  )
}
